
import React, { useState, useRef, useEffect } from "react";
import { useScriptureNavigator } from "@/hooks/useScriptureNavigator";
import { Loader2, Heart, Cross, Feather } from "lucide-react";
import ScriptureResults from "@/components/ScriptureResults";
import ScriptureSearchForm from "@/components/ScriptureSearchForm";
import { useAuth } from "@/providers/AuthProvider";
import { useScrollToTop } from "@/hooks/useScrollToTop";

const ScriptureNavigator: React.FC = () => {
  const {
    topic,
    scriptures,
    isLoading,
    isSaving,
    loadingStates,
    fetchScriptures,
    handleGetCommentary,
    saveScriptures,
    saveItem
  } = useScriptureNavigator();

  const [searchTopic, setSearchTopic] = useState("");
  const { user } = useAuth();
  const resultRef = useRef<HTMLDivElement>(null);

  // Use the scroll to top hook to ensure page starts at top every time
  useScrollToTop();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchTopic.trim()) return;
    fetchScriptures(searchTopic);
  };

  // Scroll to results when they load
  useEffect(() => {
    if (scriptures.length > 0 && !isLoading && resultRef.current) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [scriptures, isLoading]);

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="pt-24 pb-16 text-center">
        <h1 className="text-5xl md:text-7xl font-playfair mb-6">
          <span className="text-black block sm:inline">Scripture </span>
          <span className="text-heaven-gold block sm:inline">Navigator</span>
        </h1>
        <p className="text-gray-600 max-w-3xl mx-auto px-4 text-lg leading-relaxed">
          Dive deep into biblical themes and explore Scripture systematically. This tool helps you
          discover verses grouped by topic, providing a structured approach to understanding God's
          Word across different contexts.
        </p>
      </div>

      {/* Search Form Container */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-md p-8">
          <h2 className="text-3xl text-[#4169E1] text-center mb-3">
            What Topics Are You Exploring Today?
          </h2>
          <p className="text-gray-600 text-center mb-6">
            Find Bible verses by topic, with optional commentary to explore deeper meaning.
          </p>
          
          <ScriptureSearchForm
            searchTopic={searchTopic}
            setSearchTopic={setSearchTopic}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
          />
        </div>

        {/* Results Section */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 text-heaven-gold animate-spin mb-4" />
            <p className="text-heaven-gold font-medium">Finding scriptures on {searchTopic}...</p>
          </div>
        ) : (
          <>
            {scriptures.length > 0 && (
              <div ref={resultRef} className="mt-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-playfair font-bold text-gray-800 mb-2">
                    Scriptures about "{topic}"
                  </h2>
                  <p className="text-gray-600">
                    Here are {scriptures.length} scriptures that speak to this topic.
                  </p>
                </div>

                <ScriptureResults
                  scriptures={scriptures}
                  loadingStates={loadingStates}
                  onGetCommentary={handleGetCommentary}
                  onSaveAll={saveScriptures}
                  onSaveItem={saveItem}
                  isSaving={isSaving}
                  hideGuidance={true}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScriptureNavigator;
